import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, orderBy, getDocs, doc, getDoc, setDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../utils/firebase';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth } from '../utils/firebase';
import { ArrowLeft, RefreshCw, Trash2, AlertCircle, ChevronDown, ChevronUp, Link as LinkIcon } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { APP_VERSION } from '../utils/version';
import { Card, CardHeader, CardTitle, CardContent, CardDescription, CardFooter } from '@/components/ui/card';
import { restoreReportFromRemoved } from '../utils/portalSync';

const DeletedReports = () => {
  const [user, loading] = useAuthState(auth);
  const navigate = useNavigate();
  const [deletedReports, setDeletedReports] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [expandedLinks, setExpandedLinks] = useState({});

  useEffect(() => {
    if (loading) return;
    if (!user) {
      navigate('/login');
      return;
    }

    fetchDeletedReports();
  }, [user, loading]);

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      setIsMobile(width < 1024); // Increased threshold to better handle medium-sized screens
    };
    
    window.addEventListener('resize', handleResize);
    // Call once to set initial state
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const fetchDeletedReports = async () => {
    try {
      setIsLoading(true);
      const deletedReportsRef = collection(db, 'deletedReports');
      const q = query(
        deletedReportsRef,
        where('createdBy', '==', user.uid),
        orderBy('deletedAt', 'desc')
      );
      
      const snapshot = await getDocs(q);
      
      if (snapshot.empty) {
        setDeletedReports([]);
        setIsLoading(false);
        return;
      }
      
      const reportData = snapshot.docs.map(doc => {
        const data = doc.data();
        
        // Format the date for display
        let formattedDate = 'Unknown';
        if (data.deletedAt) {
          const date = new Date(data.deletedAt);
          formattedDate = `${date.toLocaleDateString()} ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
        }
        
        // Add a default resourceCount if not present (for backward compatibility)
        if (typeof data.resourceCount === 'undefined' && data.resources) {
          data.resourceCount = Array.isArray(data.resources) ? data.resources.length : 0;
        } else if (typeof data.resourceCount === 'undefined') {
          data.resourceCount = 0;
        }

        return {
          id: doc.id,
          ...data,
          deletedAt: formattedDate
        };
      });
      
      setDeletedReports(reportData);
    } catch (error) {
      console.error('Error fetching deleted reports:', error);
      toast.error('Failed to load deleted reports');
    } finally {
      setIsLoading(false);
    }
  };

  const restoreReport = async (report) => {
    try {
      // Create a simple confirmation message
      let confirmMessage = `Restore "${report.clientName}'s report"?`;
      
      if (!confirm(confirmMessage)) {
        return;
      }

      // Show loading indicator
      const loadingToastId = toast.loading('Restoring report...');

      // Create a copy of the report data without metadata fields
      const { id, _archived, originalId, deletedAt, deletedBy, imageCount, ...reportData } = report;
      
      // Ensure that updatedAt is always a string format
      if (reportData.updatedAt && typeof reportData.updatedAt !== 'string') {
        try {
          // If it has toDate method (Firebase Timestamp)
          if (reportData.updatedAt.toDate) {
            reportData.updatedAt = reportData.updatedAt.toDate().toISOString();
          } else {
            // Fallback to current timestamp if conversion fails
            reportData.updatedAt = new Date().toISOString();
          }
        } catch (err) {
          console.error('Error converting timestamp:', err);
          // Fallback to current timestamp
          reportData.updatedAt = new Date().toISOString();
        }
      }
      
      // Always mark restored reports as PAID to avoid access issues
      reportData.isPaid = true;
      
      // Validate image URLs if present
      if (reportData.imageUrls && Array.isArray(reportData.imageUrls)) {
        // Filter out any potentially invalid image entries
        reportData.imageUrls = reportData.imageUrls.filter(img => 
          img && img.url && typeof img.url === 'string' && img.url.trim() !== ''
        );
      }
      
      // Filter out file resources but keep link resources
      if (reportData.resources && Array.isArray(reportData.resources)) {
        // Keep only link resources, as file resources may no longer exist
        reportData.resources = reportData.resources.filter(resource => 
          resource && resource.type === 'link' && resource.url
        );
        
        // Update resourceCount to match the filtered resources
        reportData.resourceCount = reportData.resources.length;
        
        console.log(`Filtered resources: Keeping ${reportData.resourceCount} link resources, removing file resources`);
      }
      
      // If we have the original ID, use it, otherwise create a new ID
      const targetId = originalId || report.id;
      const inspectionRef = doc(db, 'inspections', targetId);
      
      // Check if the original ID is still available (not overwritten)
      if (originalId) {
        const docSnap = await getDoc(inspectionRef);
        if (docSnap.exists()) {
          if (!confirm(`A report with this ID already exists. Restoring will overwrite it. Continue?`)) {
            toast.error('Restore cancelled', {
              id: loadingToastId
            });
            return;
          }
        }
      }
      
      // Restore to the inspections collection
      await setDoc(inspectionRef, reportData);
      
      // Delete from the deletedReports collection
      const deletedReportRef = doc(db, 'deletedReports', report.id);
      await deleteDoc(deletedReportRef);
      
      // Update the local state
      setDeletedReports(prev => prev.filter(r => r.id !== report.id));
      
      // Try to notify portal users about the restoration
      let portalSyncSuccess = false;
      try {
        // Notify portal users about the restoration
        const result = await restoreReportFromRemoved(targetId, true);
        portalSyncSuccess = result.success;
        
        if (result.success && result.usersNotified > 0) {
          // Update loading toast with success message including notification
          toast.success(`Report restored and ${result.usersNotified} client(s) notified`, {
            id: loadingToastId
          });
        } else {
          // Basic success message if no users were notified
          toast.success('Report restored successfully', {
            id: loadingToastId
          });
        }
      } catch (portalError) {
        // Log but don't show error to user since the main operation succeeded
        console.error('Portal notification error:', portalError);
        toast.success('Report restored successfully', {
          id: loadingToastId
        });
      }
    } catch (error) {
      console.error('Error restoring report:', error);
      toast.error('Failed to restore report');
    }
  };

  const permanentlyDeleteReportHandler = async (report) => {
    try {
      // Simple confirmation with minimal details
      const confirmMessage = `Permanently delete "${report.clientName}'s report"? This cannot be undone.`;
      
      if (!confirm(confirmMessage)) {
        return;
      }
      
      // Show loading indicator
      const loadingToastId = toast.loading('Deleting report...');
      
      // Just delete directly from the DeletedReports collection
      try {
        const deletedReportRef = doc(db, 'deletedReports', report.id);
        await deleteDoc(deletedReportRef);
        
        // Update the local state immediately for fast UI feedback
        setDeletedReports(prev => prev.filter(r => r.id !== report.id));
        
        // Success message
        toast.success('Report permanently deleted', {
          id: loadingToastId
        });
      } catch (error) {
        console.error('Error deleting report:', error);
        toast.error('Failed to delete report', {
          id: loadingToastId
        });
      }
    } catch (error) {
      console.error('Error in delete handler:', error);
      toast.error('An unexpected error occurred');
    }
  };

  // Add a formatDate function if it doesn't exist
  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown date';
    
    try {
      const date = new Date(dateString);
      return date.toLocaleString('en-US', {
        month: 'numeric',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
        hour12: true
      });
    } catch (error) {
      console.error('Error formatting date:', error);
      return dateString; // Return the original string if there's an error
    }
  };

  // Toggle expanded state for resource links
  const toggleResourceLinks = (reportId) => {
    setExpandedLinks(prev => ({
      ...prev,
      [reportId]: !prev[reportId]
    }));
  };

  return (
    <div className="container mx-auto px-4 py-8 max-w-5xl">
      <Card className="bg-white shadow-md">
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <div className="flex items-center">
            <button
              onClick={() => navigate('/')}
              className="mr-4 p-2 rounded-full hover:bg-gray-100"
              aria-label="Go back"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <div>
              <CardTitle className="text-2xl font-bold text-gray-800">Deleted Reports</CardTitle>
              <p className="text-xs text-slate-500 mt-1">v{APP_VERSION}</p>
            </div>
          </div>
          <button 
            onClick={fetchDeletedReports}
            className="px-4 py-2 text-sm bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-colors flex items-center gap-2"
          >
            <RefreshCw className="h-4 w-4" />
            Refresh
          </button>
        </CardHeader>
        
        <CardContent>
          <div className="p-4 bg-blue-50 rounded-lg mb-6 text-sm text-blue-700 flex items-start">
            <AlertCircle className="h-5 w-5 mr-2 flex-shrink-0 text-blue-500" />
            <p>Removed reports can be restored to move them back to your active reports list. Permanently deleted reports cannot be recovered.</p>
          </div>
          
          {isLoading ? (
            <div className="flex justify-center items-center p-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
            </div>
          ) : deletedReports.length === 0 ? (
            <div className="bg-gray-50 rounded-lg p-12 text-center border border-gray-100">
              <Trash2 className="h-12 w-12 mx-auto text-gray-400 mb-4" />
              <p className="text-gray-600 font-medium text-lg mb-2">No deleted reports found</p>
              <p className="text-gray-500 text-sm">Removed reports will appear here</p>
            </div>
          ) : (
            <>
              {/* Desktop Table View - only for really wide screens */}
              {window.innerWidth > 1280 && (
                <div className="bg-white rounded-lg overflow-hidden border border-gray-200">
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Client
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Property Address
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Deleted At
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Images
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Resources
                          </th>
                          <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {deletedReports.map((report) => (
                          <tr key={report.id} className="hover:bg-gray-50 transition-colors">
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm font-medium text-gray-900">{report.clientName || 'No client name'}</div>
                            </td>
                            <td className="px-6 py-4">
                              <div className="text-sm text-gray-900 max-w-xs truncate">{report.propertyAddress || 'No address'}</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-gray-500">{report.deletedAt}</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm">
                                {report.imageCount > 0 ? (
                                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                                    {report.imageCount} deleted
                                  </span>
                                ) : (
                                  <span className="text-gray-500">None</span>
                                )}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm">
                                {report.resourceCount > 0 ? (
                                  <div>
                                    <div className="flex items-center">
                                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 mr-2">
                                        {report.resourceCount} resources deleted
                                      </span>
                                      {report.resources && report.resources.some(r => r.type === 'link' && r.url) && (
                                        <button 
                                          onClick={() => toggleResourceLinks(report.id)} 
                                          className="text-blue-600 hover:text-blue-800 focus:outline-none"
                                          title={expandedLinks[report.id] ? "Hide links" : "Show links"}
                                        >
                                          {expandedLinks[report.id] ? 
                                            <ChevronUp className="h-4 w-4" /> : 
                                            <ChevronDown className="h-4 w-4" />
                                          }
                                        </button>
                                      )}
                                    </div>
                                    
                                    {expandedLinks[report.id] && report.resources && (
                                      <div className="mt-2 pl-2 border-l-2 border-blue-200">
                                        {report.resources.filter(r => r.type === 'link' && r.url).map((resource, idx) => (
                                          <div key={idx} className="flex items-center text-xs text-gray-600 mb-1">
                                            <LinkIcon className="h-3 w-3 mr-1 text-blue-500" />
                                            <a 
                                              href={resource.url} 
                                              target="_blank" 
                                              rel="noopener noreferrer" 
                                              className="text-blue-600 hover:underline truncate max-w-xs"
                                              title={resource.name || resource.url}
                                            >
                                              {resource.name || resource.url}
                                            </a>
                                          </div>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                ) : (
                                  <span className="text-gray-500">None</span>
                                )}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right">
                              <div className="flex justify-end space-x-2">
                                <button
                                  onClick={() => restoreReport(report)}
                                  className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                                  title="Restore Report"
                                >
                                  <RefreshCw className="h-3.5 w-3.5 mr-1" />
                                  Restore
                                </button>
                                <button
                                  onClick={() => permanentlyDeleteReportHandler(report)}
                                  className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                                  title="Permanently Delete"
                                >
                                  <Trash2 className="h-3.5 w-3.5 mr-1" />
                                  Delete
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Card View for mobile and medium screens */}
              {(isMobile || window.innerWidth <= 1280) && (
                <div className="space-y-4">
                  {deletedReports.map((report) => (
                    <div key={report.id} className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                      <div className="p-4 border-b border-gray-100">
                        <div className="font-medium">{report.clientName || 'No client name'}</div>
                        <div className="text-sm text-gray-600 mt-1 break-words">{report.propertyAddress || 'No address'}</div>
                      </div>
                      <div className="p-4 bg-gray-50">
                        <div className="flex flex-wrap justify-between items-center gap-4">
                          <div>
                            <div className="text-xs text-gray-500">Deleted: {formatDate(report.deletedAt)}</div>
                            {report.imageCount > 0 && (
                              <div className="mt-1">
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                                  {report.imageCount} images deleted
                                </span>
                              </div>
                            )}
                            {report.resourceCount > 0 && (
                              <div className="mt-1">
                                <div className="flex items-center">
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 mr-2">
                                    {report.resourceCount} resources deleted
                                  </span>
                                  {report.resources && report.resources.some(r => r.type === 'link' && r.url) && (
                                    <button 
                                      onClick={() => toggleResourceLinks(report.id)} 
                                      className="text-blue-600 hover:text-blue-800 focus:outline-none"
                                      title={expandedLinks[report.id] ? "Hide links" : "Show links"}
                                    >
                                      {expandedLinks[report.id] ? 
                                        <ChevronUp className="h-4 w-4" /> : 
                                        <ChevronDown className="h-4 w-4" />
                                      }
                                    </button>
                                  )}
                                </div>
                                
                                {expandedLinks[report.id] && report.resources && (
                                  <div className="mt-2 pl-2 border-l-2 border-blue-200">
                                    {report.resources.filter(r => r.type === 'link' && r.url).map((resource, idx) => (
                                      <div key={idx} className="flex items-center text-xs text-gray-600 mb-1">
                                        <LinkIcon className="h-3 w-3 mr-1 text-blue-500" />
                                        <a 
                                          href={resource.url} 
                                          target="_blank" 
                                          rel="noopener noreferrer" 
                                          className="text-blue-600 hover:underline truncate max-w-xs"
                                          title={resource.name || resource.url}
                                        >
                                          {resource.name || resource.url}
                                        </a>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                          <div className="flex space-x-2 flex-shrink-0">
                            <button
                              onClick={() => restoreReport(report)}
                              className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none"
                              title="Restore Report"
                            >
                              <RefreshCw className="h-4 w-4 mr-1.5" />
                              Restore
                            </button>
                            <button
                              onClick={() => permanentlyDeleteReportHandler(report)}
                              className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none"
                              title="Permanently Delete"
                            >
                              <Trash2 className="h-4 w-4 mr-1.5" />
                              Delete
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default DeletedReports; 